import { cn } from "@/styles/utils"
import React, { useCallback } from "react"
import type {
  NativeSyntheticEvent,
  TextInputKeyPressEventData,
} from "react-native"
import { Icon, type IconName } from "../Icons"
import { FormControl } from "../gluestack/form-control"
import { Input, InputField, InputIcon, InputSlot } from "../gluestack/input"
import { FieldError } from "./FieldError"
import { FieldLabel } from "./FieldLabel"
import { useDelayedInputHandler } from "./hooks"

export const textFieldBase = `web:outline-0 
  web:outline-none 
  w-full 
  border 
  bg-white
  border-background-700 
  rounded
  data-[hover=true]:border-outline-800 
  hover:border-outline-800
  data-[focus=true]:border-outline-900
  focus:border-outline-900
  data-[disabled=true]:opacity-40 
  data-[disabled=true]:bg-background-50 
`

export const textFieldInputBase = `p-2 
  color-typography-900 
  align-text-top 
  placeholder:text-typography-300 
  placeholder:italic 
  web:cursor-text 
  web:data-[disabled=true]:cursor-not-allowed
`

// TextInput component
type TextInputProps = {
  value?: string
  handleChange: (value: string) => void
  label?: string
  name: string
  required?: boolean
  className?: string
  textInputClassName?: string
  placeholder?: string
  disabled?: boolean
  hideError?: boolean
  error?: string
  type?: "text" | "password"
  maxLength?: number
  onEnterPress?: () => void
  onFocus?: () => void
  onBlur?: () => void
  onKeyPress?: (event: NativeSyntheticEvent<TextInputKeyPressEventData>) => void
  leftIcon?: React.ElementType | IconName
  rightIcon?: React.ElementType | IconName
  onLeftIconPress?: () => void
  onRightIconPress?: () => void
}

export const TextInput = React.forwardRef<any, TextInputProps>((props, ref) => {
  const {
    value,
    handleChange,
    className,
    hideError,
    onEnterPress,
    onFocus,
    onBlur,
    onKeyPress,
    type = "text",
    placeholder,
    disabled,
    maxLength,
    label,
    required,
    error,
    textInputClassName,
    leftIcon,
    rightIcon,
    onLeftIconPress,
    onRightIconPress,
  } = props

  const { value: internalValue, handleChange: delayedHandleChange } =
    useDelayedInputHandler({
      handleChange,
      value: value || "",
    })

  const onChange = useCallback(
    (text: string) => {
      delayedHandleChange(text)
    },
    [delayedHandleChange],
  )

  const onSubmitEditing = useCallback(() => {
    if (onEnterPress) {
      onEnterPress()
    }
  }, [onEnterPress])

  return (
    <FormControl
      className={className}
      isInvalid={!!error}
      isRequired={required}
      isDisabled={disabled}
    >
      <FieldLabel label={label} required={required} />
      <Input
        isDisabled={disabled}
        isInvalid={!!error}
        isRequired={required}
        className={textFieldBase}
      >
        {leftIcon && (
          <InputSlot className="pl-3" onPress={onLeftIconPress}>
            {typeof leftIcon === "string" ? (
              <Icon name={leftIcon as IconName} />
            ) : (
              <InputIcon as={leftIcon} />
            )}
          </InputSlot>
        )}
        <InputField
          ref={ref}
          className={cn(textFieldInputBase, textInputClassName)}
          value={internalValue}
          onChangeText={onChange}
          onSubmitEditing={onSubmitEditing}
          onFocus={onFocus}
          onBlur={onBlur}
          onKeyPress={onKeyPress}
          placeholder={placeholder}
          editable={!disabled}
          maxLength={maxLength}
          secureTextEntry={type === "password"}
        />
        {rightIcon && (
          <InputSlot className="pr-3" onPress={onRightIconPress}>
            {typeof rightIcon === "string" ? (
              <Icon name={rightIcon as IconName} />
            ) : (
              <InputIcon as={rightIcon} />
            )}
          </InputSlot>
        )}
      </Input>
      {!hideError && <FieldError error={error} />}
    </FormControl>
  )
})
